import { mapListToRecords, normalizeList } from '@sophocles/entities/entityUtils';
import {
  WeeklyInternationalPowerMeterReport,
  weeklyInternationalPowerMeterReportSchema,
} from '@sophocles/entities/WeeklyInternationalPowerMeterReport/weeklyInternationalPowerMeterReportRecord';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import moment from 'moment';
import { normalize } from 'normalizr';

/* Receive Actions */
export const RECEIVE_WEEKLY_INTERNATIONAL_POWER_METER_REPORTS = 'RECEIVE_WEEKLY_INTERNATIONAL_POWER_METER_REPORTS';
function receiveWeeklyInternationalPowerMeterReports(json) {
  return {
    type: RECEIVE_WEEKLY_INTERNATIONAL_POWER_METER_REPORTS,
    weeklyInternationalPowerMeterReports: mapListToRecords(json, WeeklyInternationalPowerMeterReport),
  };
}

/* Request Actions */
// starter request
export const REQUEST_WEEKLY_INTERNATIONAL_POWER_METER_REPORT = 'REQUEST_WEEKLY_INTERNATIONAL_POWER_METER_REPORT';
function requestWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id) {
  return {
    type: REQUEST_WEEKLY_INTERNATIONAL_POWER_METER_REPORT,
    weeklyInternationalPowerMeterReport_id,
  };
}

export const REQUEST_WEEKLY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE =
  'REQUEST_WEEKLY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE';
function requestWeeklyInternationalPowerMeterReportsForSiteWithRange(site_id, from, to) {
  return {
    type: REQUEST_WEEKLY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE,
    site_id,
    from,
    to,
  };
}

/* Fetch Actions */
// starter fetch

export function fetchWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id) {
  return dispatch => {
    dispatch(requestWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id));
    return apiClient
      .get(`/weeklyinternationalpowermeterreports/${weeklyInternationalPowerMeterReport_id}/`)
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(response.data, 'weeklyInternationalPowerMeterReports', weeklyInternationalPowerMeterReportSchema),
      )
      .then(json =>
        dispatch(receiveWeeklyInternationalPowerMeterReports(json.entities.weeklyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, REQUEST_WEEKLY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

export function fetchWeeklyInternationalPowerMeterReportsForSiteWithRange(site_id, from, to) {
  const fromTS = startOfDay(parseISO(from));
  const toTS = endOfDay(parseISO(to));
  return dispatch => {
    dispatch(requestWeeklyInternationalPowerMeterReportsForSiteWithRange(site_id, fromTS, toTS));
    return apiClient
      .get(
        `/weeklyinternationalpowermeterreports/?site=${site_id}&report_date__gte=${format(
          fromTS,
          'yyyy-MM-dd',
        )}&report_date__lte=${format(toTS, 'yyyy-MM-dd')}`,
      )
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(
          response.data.results,
          'weeklyInternationalPowerMeterReports',
          weeklyInternationalPowerMeterReportSchema,
        ),
      )
      .then(json =>
        dispatch(receiveWeeklyInternationalPowerMeterReports(json.entities.weeklyInternationalPowerMeterReports)),
      )
      .catch(error =>
        dispatch(genericErrorAction(error, REQUEST_WEEKLY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE)),
      );
  };
}

/* Edit Actions */
export const EDIT_WEEKLY_INTERNATIONAL_POWER_METER_REPORT = 'EDIT_WEEKLY_INTERNATIONAL_POWER_METER_REPORT';
export function editWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id) {
  return {
    type: EDIT_WEEKLY_INTERNATIONAL_POWER_METER_REPORT,
    weeklyInternationalPowerMeterReport: weeklyInternationalPowerMeterReport_id,
  };
}

export function patchWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id, values) {
  return dispatch => {
    dispatch(editWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id));
    return apiClient
      .patch(`/weeklyinternationalpowermeterreports/${weeklyInternationalPowerMeterReport_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, weeklyInternationalPowerMeterReportSchema))
      .then(json =>
        dispatch(receiveWeeklyInternationalPowerMeterReports(json.entities.weeklyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, EDIT_WEEKLY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

/* Create Actions */
export const CREATE_WEEKLY_INTERNATIONAL_POWER_METER_REPORT = 'CREATE_WEEKLY_INTERNATIONAL_POWER_METER_REPORT';
function sayCreateWeeklyInternationalPowerMeterReport(values) {
  return {
    type: CREATE_WEEKLY_INTERNATIONAL_POWER_METER_REPORT,
    values: values,
  };
}

export function createWeeklyInternationalPowerMeterReport(values) {
  return dispatch => {
    dispatch(sayCreateWeeklyInternationalPowerMeterReport(values));
    return apiClient
      .post(`/weeklyinternationalpowermeterreports/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, weeklyInternationalPowerMeterReportSchema))
      .then(json =>
        dispatch(receiveWeeklyInternationalPowerMeterReports(json.entities.weeklyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, CREATE_WEEKLY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

/* Delete Actions */
export const DELETE_WEEKLY_INTERNATIONAL_POWER_METER_REPORT = 'DELETE_WEEKLY_INTERNATIONAL_POWER_METER_REPORT';
function sayDeleteWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id) {
  return {
    type: DELETE_WEEKLY_INTERNATIONAL_POWER_METER_REPORT,
    weeklyInternationalPowerMeterReport: weeklyInternationalPowerMeterReport_id,
  };
}

export function deleteWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id) {
  return dispatch =>
    apiClient
      .delete(`/weeklyinternationalpowermeterreports/${weeklyInternationalPowerMeterReport_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteWeeklyInternationalPowerMeterReport(weeklyInternationalPowerMeterReport_id)))
      .catch(error => dispatch(genericErrorAction(error, DELETE_WEEKLY_INTERNATIONAL_POWER_METER_REPORT)));
}
